import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { TAB_LABELS, notificationTabId } from '@/utils/notificationRouting';
import { Bell, Settings, Check, CheckCheck, X, Clock, AlertTriangle, CheckCircle, Info, Trash2, Star, StarOff } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DraggableModal } from "@/components/ui/draggable-modal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { API } from "@/config";

// API artık merkezi config'ten (proxy/8000); REACT_APP_BACKEND_URL tek başına kullanılmıyor

const NotificationPanel = ({
  personnelId,
  personnelName,
  // Sidebar rozetleriyle AYNI kaynak: {sekmeId: okunmamış adet}
  badgesByTab = {},
  // Bildirime tıklanınca ilgili sekmeye götür
  onNavigate,
  // Okundu/silme sonrası sidebar sayaçlarını tazele
  onSummaryChanged
}) => {
  const [notifications, setNotifications] = useState([]);
  const [moduleFilter, setModuleFilter] = useState('all'); // 'all' | sekme id
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState('all'); // 'all', 'read', 'old'
  const [settings, setSettings] = useState({
    enabled: true,
    delay_notifications: true,
    summary_notifications: true,
    urgent_notifications: true,
    success_notifications: true,
    auto_delete_days: 30, // Otomatik silme süresi (gün)
    max_notifications: 100 // Maksimum bildirim sayısı
  });

  // Uygulama içi bildirimleri yükle (vekalet, mesai vb. – in_app_notifications)
  const normalizeInApp = (list) => (list || []).map((n) => ({
    id: n.id,
    title: n.title || 'Bildirim',
    message: n.message || '',
    created_at: n.timestamp || n.created_at,
    read: n.is_read === true,
    type: n.notification_type || n.type || 'summary',
    important: n.important === true,
    // Modül/sekme yönlendirmesi için ham alanlar korunur (eskiden atılıyordu)
    related_module: n.related_module || null,
    notification_type: n.notification_type || n.type || null,
    tabId: notificationTabId(n)
  }));

  const loadNotifications = async () => {
    if (!personnelId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/notifications/in-app/${personnelId}`, {
        params: { limit: 30 }
      });
      
      if (response.data.success && Array.isArray(response.data.notifications)) {
        setNotifications(normalizeInApp(response.data.notifications));
        setUnreadCount(response.data.unread_count ?? 0);
      }
    } catch (error) {
      console.error('Bildirimler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // Ayarları yükle
  const loadSettings = async () => {
    if (!personnelId) return;
    
    try {
      const response = await axios.get(`${API}/api/notifications/settings/${personnelId}`);
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Bildirim ayarları yüklenirken hata:', error);
    }
  };

  // Ayarları kaydet
  const saveSettings = async (newSettings) => {
    try {
      const response = await axios.put(`${API}/api/notifications/settings/${personnelId}`, {
        personnel_id: personnelId,
        ...newSettings
      });
      
      if (response.data.success) {
        setSettings(newSettings);
        toast.success('Bildirim ayarları kaydedildi');
      }
    } catch (error) {
      console.error('Bildirim ayarları kaydedilirken hata:', error);
      toast.error('Ayarlar kaydedilemedi');
    }
  };

  // Bildirimi okundu işaretle (in-app API)
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/api/notifications/mark-read/${notificationId}`, null, {
        params: { personnel_id: personnelId }
      });
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      onSummaryChanged?.();
    } catch (error) {
      console.error('Bildirim işaretlenirken hata:', error);
    }
  };

  // Tümünü okundu işaretle (in-app API)
  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/api/notifications/mark-all-read/${personnelId}`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      onSummaryChanged?.();
      toast.success('Tüm bildirimler okundu olarak işaretlendi');
    } catch (error) {
      console.error('Bildirimler işaretlenirken hata:', error);
    }
  };

  // Modül çipleri — sidebar rozetleriyle aynı sayılar, yalnız dolu olanlar.
  const moduleChips = useMemo(
    () => Object.entries(badgesByTab)
      .filter(([, count]) => count > 0)
      .map(([tabId, count]) => ({ tabId, count, label: TAB_LABELS[tabId] || tabId }))
      .sort((a, b) => b.count - a.count),
    [badgesByTab]
  );

  const visibleNotifications = useMemo(
    () => (moduleFilter === 'all'
      ? notifications
      : notifications.filter(n => n.tabId === moduleFilter)),
    [notifications, moduleFilter]
  );

  // Seçili çip artık listede yoksa (hepsi okundu) filtreyi sıfırla.
  useEffect(() => {
    if (moduleFilter !== 'all' && !moduleChips.some(c => c.tabId === moduleFilter)) {
      setModuleFilter('all');
    }
  }, [moduleChips, moduleFilter]);

  // Bildirime tıklandığında: okundu yap + ilgili sekmeye git + paneli kapat.
  const openNotification = (notification) => {
    if (!notification.read) markAsRead(notification.id);
    if (notification.tabId && onNavigate) {
      onNavigate(notification.tabId);
      setIsOpen(false);
    }
  };

  // Bildirimi önemli işaretle/kaldır
  const toggleImportant = async (notificationId, currentImportant) => {
    try {
      await axios.put(`${API}/api/notifications/${notificationId}/important`, {
        important: !currentImportant
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, important: !currentImportant } : n)
      );
      toast.success(currentImportant ? 'Önemli işareti kaldırıldı' : 'Önemli olarak işaretlendi');
    } catch (error) {
      console.error('Bildirim işaretlenirken hata:', error);
    }
  };

  // Bildirimleri sil
  const deleteNotifications = async (type) => {
    try {
      let endpoint = `${API}/api/notifications/in-app/delete/${personnelId}`;
      let params = {};
      
      if (type === 'all') {
        params.delete_all = true;
        params.keep_important = true; // Önemli olanları koru
      } else if (type === 'all_force') {
        // Önemli (⭐) işaretliler DÂHİL her şeyi sil. Operasyon/OBT gibi bazı
        // bildirimler sistem tarafından otomatik "önemli" oluşturulur; kullanıcı
        // bunları "önemli hariç" seçenekleriyle temizleyemiyordu → bu seçenek şart.
        params.delete_all = true;
        params.keep_important = false;
      } else if (type === 'read') {
        params.delete_read = true;
        params.keep_important = true;
      } else if (type === 'old') {
        params.delete_older_than_days = 7; // Son 7 gün hariç
        params.keep_important = true;
      }
      
      const response = await axios.delete(endpoint, { params });
      
      if (response.data.success) {
        loadNotifications();
        onSummaryChanged?.();
        toast.success(`${response.data.deleted_count} bildirim silindi`);
      }
    } catch (error) {
      console.error('Bildirimler silinirken hata:', error);
      toast.error('Bildirimler silinemedi');
    }
    setDeleteDialogOpen(false);
  };

  // Tek bildirim sil (in-app API)
  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/api/notifications/in-app/${notificationId}`, {
        params: { personnel_id: personnelId }
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
      onSummaryChanged?.();
      toast.success('Bildirim silindi');
    } catch (error) {
      console.error('Bildirim silinirken hata:', error);
    }
  };

  // Periyodik yükleme
  useEffect(() => {
    loadNotifications();
    loadSettings();
    
    // Her 60 saniyede bir kontrol et
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, [personnelId]);

  // Bildirim ikonu
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'delay': return <Clock className="h-4 w-4 text-orange-500" />;
      case 'urgent': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'success': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'summary': return <Info className="h-4 w-4 text-blue-500" />;
      default: return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  // Bildirim arka plan rengi
  const getNotificationBg = (type, read) => {
    if (read) return 'bg-white';
    switch (type) {
      case 'delay': return 'bg-orange-50';
      case 'urgent': return 'bg-red-50';
      case 'success': return 'bg-green-50';
      case 'summary': return 'bg-blue-50';
      default: return 'bg-gray-50';
    }
  };

  // Zaman formatla
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dk önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR');
  };

  if (!settings.enabled) {
    return (
      <Button 
        variant="ghost" 
        size="icon" 
        className="relative"
        onClick={() => setSettingsOpen(true)}
      >
        <Bell className="h-5 w-5 text-gray-400" />
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-gray-400 rounded-full" />
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge 
                className="absolute -top-1 -right-1 h-5 min-w-5 px-1 bg-red-500 text-white text-xs flex items-center justify-center"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="end" className="w-80 p-0">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-slate-50">
            <h3 className="font-semibold text-sm">🔔 Bildirimler ({notifications.length})</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={markAllAsRead}
                >
                  <CheckCheck className="h-3 w-3 mr-1" />
                  Oku
                </Button>
              )}
              {notifications.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Sil
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7"
                onClick={() => { setIsOpen(false); setSettingsOpen(true); }}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Modül çipleri — hangi sekmeden kaç bildirim var (sidebar ile aynı sayı) */}
          {moduleChips.length > 0 && (
            <div className="flex flex-wrap gap-1 border-b px-2 py-1.5">
              <button
                type="button"
                onClick={() => setModuleFilter('all')}
                className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                  moduleFilter === 'all'
                    ? 'border-slate-700 bg-slate-700 text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Tümü
              </button>
              {moduleChips.map(chip => (
                <button
                  key={chip.tabId}
                  type="button"
                  onClick={() => setModuleFilter(chip.tabId)}
                  title={`${chip.label} sekmesine git`}
                  onDoubleClick={() => { onNavigate?.(chip.tabId); setIsOpen(false); }}
                  className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                    moduleFilter === chip.tabId
                      ? 'border-slate-700 bg-slate-700 text-white'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="max-w-[110px] truncate">{chip.label}</span>
                  <span className={`rounded-full px-1 font-semibold ${
                    moduleFilter === chip.tabId ? 'bg-white/20' : 'bg-red-100 text-red-600'
                  }`}>
                    {chip.count > 99 ? '99+' : chip.count}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Bildirim Listesi */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                Yükleniyor...
              </div>
            ) : visibleNotifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>{moduleFilter === 'all' ? 'Henüz bildirim yok' : 'Bu modülde gösterilecek bildirim yok'}</p>
              </div>
            ) : (
              visibleNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 border-b hover:bg-slate-50 transition-colors ${getNotificationBg(notification.type, notification.read)} ${notification.important ? 'border-l-4 border-l-yellow-400' : ''}`}
                >
                  <div className="flex gap-3">
                    <div className="mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={`text-sm cursor-pointer ${notification.read ? 'text-muted-foreground' : 'font-medium'}`}
                          onClick={() => openNotification(notification)}
                          title={notification.tabId ? `${TAB_LABELS[notification.tabId] || notification.tabId} sekmesine git` : undefined}
                        >
                          {notification.title}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!notification.read && (
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleImportant(notification.id, notification.important);
                            }}
                            className="p-1 hover:bg-gray-100 rounded"
                            title={notification.important ? "Önemli işaretini kaldır" : "Önemli işaretle"}
                          >
                            {notification.important ? (
                              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                            ) : (
                              <StarOff className="h-3 w-3 text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Sil"
                          >
                            <X className="h-3 w-3 text-gray-400 hover:text-red-500" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                        <span>{formatTime(notification.created_at)}</span>
                        {notification.tabId && (
                          <span className="rounded bg-gray-100 px-1 py-px text-[10px] text-gray-500">
                            {TAB_LABELS[notification.tabId] || notification.tabId}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Ayarlar Modal */}
      <DraggableModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="🔔 Bildirim Ayarları"
        subtitle="Hangi bildirimleri almak istediğinizi seçin"
        maxWidth="max-w-md"
      >
          <div className="space-y-4">
            {/* Ana Aç/Kapa */}
            <div className="flex items-center justify-between p-3 rounded-lg border-2 border-blue-200 bg-blue-50">
              <div>
                <Label className="text-base font-medium">Bildirimler</Label>
                <p className="text-sm text-muted-foreground">Tüm bildirimleri aç/kapat</p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => {
                  const newSettings = { ...settings, enabled: checked };
                  saveSettings(newSettings);
                }}
              />
            </div>
            
            {settings.enabled && (
              <>
                {/* Geciken Görev Bildirimleri */}
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-orange-500" />
                    <div>
                      <Label>Geciken Görevler</Label>
                      <p className="text-xs text-muted-foreground">Görev süresi dolduğunda bildir</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.delay_notifications}
                    onCheckedChange={(checked) => {
                      const newSettings = { ...settings, delay_notifications: checked };
                      saveSettings(newSettings);
                    }}
                  />
                </div>
                
                {/* Acil Durum Bildirimleri */}
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    <div>
                      <Label>Acil Durumlar</Label>
                      <p className="text-xs text-muted-foreground">Kritik sorunlarda bildir</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.urgent_notifications}
                    onCheckedChange={(checked) => {
                      const newSettings = { ...settings, urgent_notifications: checked };
                      saveSettings(newSettings);
                    }}
                  />
                </div>
                
                {/* Başarı Bildirimleri */}
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                      <Label>Başarı Bildirimleri</Label>
                      <p className="text-xs text-muted-foreground">Görevler tamamlandığında bildir</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.success_notifications}
                    onCheckedChange={(checked) => {
                      const newSettings = { ...settings, success_notifications: checked };
                      saveSettings(newSettings);
                    }}
                  />
                </div>
                
                {/* Günlük Özet Bildirimleri */}
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Info className="h-5 w-5 text-blue-500" />
                    <div>
                      <Label>Günlük Özet</Label>
                      <p className="text-xs text-muted-foreground">Günlük performans özeti</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.summary_notifications}
                    onCheckedChange={(checked) => {
                      const newSettings = { ...settings, summary_notifications: checked };
                      saveSettings(newSettings);
                    }}
                  />
                </div>

                {/* Otomatik Temizleme Ayarları */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Trash2 className="h-4 w-4" />
                    Otomatik Temizleme
                  </h4>
                  
                  <div className="space-y-3">
                    {/* Maksimum Bildirim Sayısı */}
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <Label>Maksimum Bildirim</Label>
                        <p className="text-xs text-muted-foreground">En fazla tutulacak bildirim sayısı</p>
                      </div>
                      <SearchableSelect
                        value={String(settings.max_notifications || 100)}
                        onChange={(value) => {
                          const newSettings = { ...settings, max_notifications: parseInt(value) };
                          saveSettings(newSettings);
                        }}
                        className="w-24"
                        showAllOption={false}
                        placeholder="Seç"
                        searchPlaceholder="Ara..."
                        options={[
                          { id: "50", name: "50" },
                          { id: "100", name: "100" },
                          { id: "200", name: "200" },
                          { id: "500", name: "500" },
                        ]}
                      />
                    </div>
                    
                    {/* Otomatik Silme Süresi */}
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <Label>Otomatik Silme</Label>
                        <p className="text-xs text-muted-foreground">Eski bildirimleri otomatik sil</p>
                      </div>
                      <SearchableSelect
                        value={String(settings.auto_delete_days || 30)}
                        onChange={(value) => {
                          const newSettings = { ...settings, auto_delete_days: parseInt(value) };
                          saveSettings(newSettings);
                        }}
                        className="w-28"
                        showAllOption={false}
                        placeholder="Seç"
                        searchPlaceholder="Ara..."
                        options={[
                          { id: "1", name: "1 gün" },
                          { id: "3", name: "3 gün" },
                          { id: "7", name: "7 gün" },
                          { id: "14", name: "14 gün" },
                          { id: "30", name: "30 gün" },
                          { id: "60", name: "60 gün" },
                          { id: "0", name: "Silme" },
                        ]}
                      />
                    </div>
                    
                    <p className="text-xs text-muted-foreground bg-yellow-50 p-2 rounded border border-yellow-200">
                      ⭐ Önemli işaretli bildirimler otomatik silinmez
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
      </DraggableModal>

      {/* Silme Onay Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>🗑️ Bildirimleri Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Hangi bildirimleri silmek istiyorsunuz? İlk üç seçenek önemli (⭐) işaretli
              bildirimleri korur; en alttaki seçenek onları da siler.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-2 py-4">
            <Button
              variant="outline"
              className="w-full justify-start text-red-600 hover:bg-red-50"
              onClick={() => deleteNotifications('all')}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Tümünü Sil (önemli hariç)
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => deleteNotifications('read')}
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Okunmuşları Sil
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => deleteNotifications('old')}
            >
              <Clock className="h-4 w-4 mr-2" />
              Son 7 Gün Hariç Sil
            </Button>
            <div className="pt-2 mt-1 border-t">
              <Button
                variant="outline"
                className="w-full justify-start text-red-700 border-red-300 hover:bg-red-50"
                onClick={() => deleteNotifications('all_force')}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Tümünü Sil (önemli ⭐ dâhil)
              </Button>
              <p className="text-[11px] text-muted-foreground mt-1 px-1">
                Operasyon/OBT gibi otomatik önemli işaretlenen bildirimleri de kaldırır.
              </p>
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default NotificationPanel;
